import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useMapUnitModal } from './hooks/useMapUnitModal';
import { UnitModal, UnitSubmitData } from '../unit/UnitModal';
import { MapViewMode } from './MapViewTypes';
import { MapEntity } from './mapTypes.d';
import { RootState } from '../../store';
import * as markerService from '../../services/markerService';

interface MapUnitModalTriggerProps {
    /** Current map view mode */
    editMode: MapViewMode;
    /** Callback when a new marker is created */
    handleNewMarker: (entity: MapEntity) => void;
}

/**
 * Component that handles map click events and renders the unified UnitModal.
 * On submit, creates a marker via the backend API. The backend persists the marker
 * and publishes it via MQTT, which other clients receive in real-time.
 * The local map entity is also added immediately for responsiveness.
 */
export function MapUnitModalTrigger({ editMode, handleNewMarker }: MapUnitModalTriggerProps) {
    const modal = useMapUnitModal(editMode);
    const { eventId } = useParams<{ eventId: string }>();
    const selectedFactionId = useSelector((state: RootState) => state.orbat.selectedFactionId);

    const handleSubmit = useCallback(async (data: UnitSubmitData) => {
        if (!data.position || !eventId) return;

        const factionId = data.isGlobal ? null : selectedFactionId;

        if (!data.isGlobal && !selectedFactionId) {
            alert('Please select a faction before adding faction-scoped markers to the map.');
            return;
        }

        try {
            // Strip the derived overlays before persisting: `dtg` is derived
            // from position_time and `uniqueDesignation` from the entity name,
            // so neither is a real text modifier. The backend strips them too
            // (DERIVED_MODIFIER_KEYS), but keeping the name out of the modifier
            // map here is what stops the duplicate-identity problem coming back.
            const {
                dtg: _ignoredDtg,
                uniqueDesignation: _ignoredName,
                ...textModifiers
            } = (data.markerProperties ?? {}) as Record<string, unknown>;

            // Create marker on the backend — backend will publish via MQTT
            const marker = await markerService.createMarker(eventId, {
                // A marker's name is optional — blank means "draw no
                // designation label". It must NOT fall back to the SIDC: that
                // put a 20-digit machine code in a human-name field, and once
                // the map started deriving its label from `name`, those codes
                // appeared next to the symbols.
                name: data.name ?? '',
                sidc: data.sidc,
                faction_id: factionId,
                latitude: data.position.lat,
                longitude: data.position.lng,
                text_modifiers: textModifiers,
            });

            // Also add to local store immediately for responsiveness
            const entity: MapEntity = {
                id: marker.id,
                latLng: { lat: data.position.lat, lng: data.position.lng },
                code: marker.sidc,
                own: true,
                name: marker.name,
                markerProperties: data.markerProperties,
            };
            handleNewMarker(entity);
        } catch (error) {
            console.error('Failed to create marker:', error);
        }
    }, [handleNewMarker, eventId, selectedFactionId]);

    return (
        <UnitModal
            show={modal.show}
            onHide={modal.onHide}
            context="map"
            position={modal.position}
            onSubmit={handleSubmit}
            showNameField={true}
            showTextModifiers={true}
        />
    );
}

export default MapUnitModalTrigger;
